import { ChangeEvent, FC, useCallback, useReducer } from "react";
import {
    Collapse,
    FormControl,
    Image,
    List,
    ModalConfirm,
    Note,
    Notification,
    Stack,
    TextInput,
    TextLink,
} from "@contentful/f36-components";
import { HelpCircleIcon } from "@contentful/f36-icons";
import { useSDK } from "@contentful/react-apps-toolkit";
import { UploadGlossaryButton } from "..";
import { BadGlossaryLocalesWarning } from "..";
import { useCreateGlossary, useProcessCSV } from "../../hooks";
import { Glossary, GlossaryLocalePair } from "../../lib";
import { useFetchDeeplGlossaries } from "../../services";
import { defaultGlossaryName } from "../../utils";

export interface GlossaryModalProps {
    isShown: boolean;
    setIsShown: React.Dispatch<React.SetStateAction<boolean>>;
    glossaries: Glossary[];
}

type State = {
    file: File | null;
    name: string;
    badLocales: GlossaryLocalePair[];
    goodLocales: GlossaryLocalePair[];
    uploadError: string | null;
    isUploading: boolean;
    isCreating: boolean;
};

const initialState: State = {
    file: null,
    name: "",
    badLocales: [],
    goodLocales: [],
    uploadError: null,
    isUploading: false,
    isCreating: false,
};

type Action =
    | { type: "SET_FILE"; payload: File | null }
    | { type: "SET_NAME"; payload: string }
    | { type: "SET_IS_UPLOADING"; payload: boolean }
    | { type: "SET_IS_CREATING"; payload: boolean }
    | { type: "UPLOAD_ERROR"; payload: { errorMsg: string } }
    | {
          type: "UPLOAD_SUCCESS";
          payload: Pick<State, "goodLocales" | "badLocales" | "name" | "file">;
      }
    | { type: "RESET" };

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case "SET_FILE":
            return { ...state, file: action.payload };
        case "SET_NAME":
            return { ...state, name: action.payload };
        case "SET_IS_UPLOADING":
            return { ...state, isUploading: action.payload };
        case "SET_IS_CREATING":
            return { ...state, isCreating: action.payload };
        case "UPLOAD_ERROR":
            return { ...state, file: null, uploadError: action.payload.errorMsg };
        case "UPLOAD_SUCCESS":
            return { ...state, uploadError: null, ...action.payload };
        case "RESET":
            return { ...state, file: null, uploadError: null };
        default:
            return state;
    }
};

export const ImportGlossaryModal: FC<GlossaryModalProps> = ({
    isShown,
    setIsShown,
    glossaries,
}) => {
    const sdk = useSDK();

    const { processCSV } = useProcessCSV();
    const { createGlossary } = useCreateGlossary();
    const { mutate: glossariesMutation } = useFetchDeeplGlossaries();

    const [
        { file, name, badLocales, goodLocales, uploadError, isUploading, isCreating },
        dispatch,
    ] = useReducer(reducer, initialState);

    const nameInputRef = useCallback((node: HTMLInputElement | null) => {
        node?.focus();
        node?.select();
    }, []);

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) {
            dispatch({ type: "SET_FILE", payload: null });
            return;
        }

        dispatch({ type: "SET_IS_UPLOADING", payload: true });
        try {
            const selectedFile = event.target.files[0];
            if (selectedFile.type !== "text/csv") {
                throw new Error("CSV file expected");
            }

            const { goodGlossaryLocales, badGlossaryLocales } = await processCSV(selectedFile);

            dispatch({
                type: "UPLOAD_SUCCESS",
                payload: {
                    goodLocales: goodGlossaryLocales,
                    badLocales: badGlossaryLocales,
                    name: defaultGlossaryName(selectedFile.name),
                    file: selectedFile,
                },
            });
        } catch (error) {
            dispatch({
                type: "UPLOAD_ERROR",
                payload: {
                    errorMsg:
                        error instanceof Error
                            ? error.message
                            : "An unknown error has occurred while processing the file",
                },
            });
        }
        dispatch({ type: "SET_IS_UPLOADING", payload: false });
    };

    const resetModal = () => {
        setIsShown(false);
        dispatch({ type: "RESET" });
    };

    const onConfirm = async () => {
        dispatch({ type: "SET_IS_CREATING", payload: true });
        try {
            if (!file) {
                throw new Error("No glossary file found");
            }
            if (glossaries.find(glossary => glossary.name === name)) {
                throw new Error(
                    `Glossary "${name}" already exists. Please choose a different name`
                );
            }
            await createGlossary(name, file, goodLocales);
            await glossariesMutation();
            resetModal();
            Notification.success(
                `Don't forget to select it and click "Save" to use your new glossary`,
                {
                    title: `Successfully imported glossary "${name}"!`,
                    duration: 0,
                }
            );
        } catch (error) {
            if (error instanceof Error) {
                sdk.notifier.error(error.message);
            } else {
                sdk.notifier.error(
                    `An unknown error has occurred while importing the glossary "${name}"`
                );
            }
        }
        dispatch({ type: "SET_IS_CREATING", payload: false });
    };

    return (
        <ModalConfirm
            title="Import glossary"
            intent="primary"
            size="large"
            isConfirmDisabled={!file || name.length === 0}
            isConfirmLoading={isCreating}
            shouldCloseOnEscapePress={!file}
            shouldCloseOnOverlayClick={!file}
            isShown={isShown}
            onCancel={resetModal}
            onConfirm={onConfirm}>
            <Stack flexDirection="column" alignItems="start">
                <Collapse isExpanded={!file} style={{ width: "100%" }}>
                    <Stack flexDirection="column" alignItems="start">
                        <List as="ol">
                            <List.Item>
                                Go to{" "}
                                <TextLink
                                    href="https://www.deepl.com/glossary"
                                    target="_blank"
                                    rel="noopener noreferrer">
                                    https://www.deepl.com/glossary
                                </TextLink>
                            </List.Item>
                            <List.Item>
                                Download your glossary as described in the graphic below
                                <Image
                                    src="glossaryExport.png"
                                    alt="Screenshot of DeepL website showing where to download a glossary"
                                    width="603px"
                                    height="188px"
                                />
                            </List.Item>
                        </List>
                        <Note variant="neutral" icon={<HelpCircleIcon />} style={{ width: "100%" }}>
                            Can't find this? Exporting glossaries requires a{" "}
                            <strong>DeepL Pro Advanced</strong> subscription or higher.
                        </Note>
                    </Stack>
                </Collapse>
                <UploadGlossaryButton
                    value={file}
                    onChange={handleFileChange}
                    isLoading={isUploading}
                    errorMessage={uploadError}
                />
                <Collapse isExpanded={!!file} style={{ width: "100%" }}>
                    <Stack flexDirection="column" alignItems="start">
                        <BadGlossaryLocalesWarning badLocales={badLocales} />
                        <FormControl>
                            <FormControl.Label>Glossary name</FormControl.Label>
                            <TextInput
                                ref={nameInputRef}
                                isInvalid={name.length === 0}
                                value={name}
                                onChange={e =>
                                    dispatch({ type: "SET_NAME", payload: e.target.value })
                                }
                                autoFocus
                            />
                            {name.length === 0 && (
                                <FormControl.ValidationMessage>
                                    Please choose a name for your glossary
                                </FormControl.ValidationMessage>
                            )}
                        </FormControl>
                    </Stack>
                </Collapse>
            </Stack>
        </ModalConfirm>
    );
};
